"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { METRICS_EVENTS } from "@cede/types";
import { DISCORD_INVITE_URL, TWITTER_URL } from "@cede/utils";
import { DiscordIcon, XIcon } from "@/components/icons";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { API_HEALTH_STATUS, type ApiHealthStatus } from "@/hooks/health";
import { useDevice, useDraggable, type Position } from "@/hooks/ui";
import { cn } from "@/lib/utils";
import { Loader2, WifiOff, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

type ApiHealthBannerProps = {
  status: ApiHealthStatus;
};

const BOUNDS = { padding: 4, elementSize: 60 };

function getDefaultPosition(): Position {
  if (typeof window === "undefined") return { x: 12, y: 4 };
  return { x: Math.max(12, (window.innerWidth - 300) / 2), y: 4 };
}

function clampPosition(pos: Position): Position {
  if (typeof window === "undefined") return pos;
  const maxX = window.innerWidth - BOUNDS.elementSize;
  const maxY = window.innerHeight - BOUNDS.elementSize;
  return {
    x: Math.max(BOUNDS.padding, Math.min(pos.x, maxX)),
    y: Math.max(BOUNDS.padding, Math.min(pos.y, maxY)),
  };
}

/**
 * Non-blocking warning banner for API health issues.
 * Single draggable container that smoothly animates between collapsed/expanded states.
 */
export function ApiHealthBanner({ status }: ApiHealthBannerProps) {
  const { trackEvent } = useMetricsEvents();
  const { isMobile } = useDevice();
  const [collapsed, setCollapsed] = useState(false);
  const prevStatusRef = useRef(status);
  if (prevStatusRef.current !== status) {
    prevStatusRef.current = status;
    if (collapsed) setCollapsed(false);
  }
  const [position, setPosition] = useState<Position>(getDefaultPosition);
  const [isNavigatorOnline, setIsNavigatorOnline] = useState(() => {
    if (typeof navigator === "undefined") return true;
    return navigator.onLine;
  });

  const isOfflineStatus = status === API_HEALTH_STATUS.OFFLINE;
  const isOfflineConfirmed = isOfflineStatus && !isNavigatorOnline;
  const title = isOfflineConfirmed
    ? "You're offline"
    : isOfflineStatus
      ? "Connection issue detected"
      : "Cede Hub is experiencing issues";
  const subtitle = isOfflineConfirmed
    ? "Check your internet connection"
    : isOfflineStatus
      ? "Trying to reconnect. Some features may be unavailable."
      : "Some features may be unavailable. We're working on it.";

  const containerRef = useRef<HTMLDivElement>(null);

  // Always draggable
  const { isDragging, handleMouseDown } = useDraggable({
    elementRef: containerRef,
    enabled: true,
    position,
    onPositionChange: setPosition,
    clampPosition,
    positionOrigin: "top-left",
    enableMouse: true,
  });

  // Track navigator.onLine changes to avoid stale offline copy.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const updateOnlineStatus = () => setIsNavigatorOnline(navigator.onLine);
    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);
    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
    };
  }, []);

  // Clamp position on resize
  useEffect(() => {
    const handleResize = () => setPosition((prev) => clampPosition(prev));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleToggle = useCallback(() => {
    if (isDragging) return;
    const newCollapsed = !collapsed;
    setCollapsed(newCollapsed);
    if (newCollapsed) {
      trackEvent(METRICS_EVENTS.API_DOWN_BANNER_DISMISSED, { status, action: "collapse" });
    }
  }, [isDragging, collapsed, trackEvent, status]);

  const handleDiscordClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      trackEvent(METRICS_EVENTS.API_DOWN_DISCORD_CLICKED, { status });
    },
    [trackEvent, status],
  );

  const handleXClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      trackEvent(METRICS_EVENTS.API_DOWN_X_CLICKED, { status });
    },
    [trackEvent, status],
  );

  // Mobile collapsed: icon only
  const isMobileCollapsed = isMobile && collapsed;

  return (
    <div
      ref={containerRef}
      className={cn(
        "fixed z-snackbar left-0 top-0",
        "rounded-xl",
        "bg-orange-950/30 backdrop-blur-md shadow-lg",
        "border border-cede-hr",
        "cursor-grab select-none will-change-transform",
        "transition-all duration-300 ease-out",
        isDragging && "cursor-grabbing",
        // Size classes
        collapsed ? "px-2 py-1" : "p-3",
        !collapsed && "w-[calc(100vw-2rem)] max-w-md",
      )}
      style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
      onMouseDown={handleMouseDown}
    >
      {/* Collapsed: Mobile (icon only) */}
      {isMobileCollapsed && (
        <button
          className="flex items-center justify-center"
          onClick={handleToggle}
          aria-label="Expand health status banner"
        >
          {isOfflineConfirmed ? (
            <WifiOff className="h-5 w-5 text-cede-error" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-cede-error" />
          )}
        </button>
      )}

      {/* Collapsed: Desktop (pill with text) */}
      {collapsed && !isMobile && (
        <div className="flex items-center gap-2">
          {isOfflineConfirmed ? (
            <WifiOff className="h-4 w-4 text-cede-error" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-cede-error" />
          )}
          <span className="text-sm font-medium text-cede-text-primary whitespace-nowrap">{title}</span>
          {!isOfflineConfirmed && <Loader2 className="h-3.5 w-3.5 animate-spin text-cede-text-secondary" />}
          <button
            onClick={handleToggle}
            className="ml-1 rounded-lg p-1 hover:bg-cede-primary/10 transition-colors"
            aria-label="Expand banner"
          >
            <ChevronDown className="h-4 w-4 text-cede-text-secondary" />
          </button>
        </div>
      )}

      {/* Expanded view */}
      {!collapsed && (
        <div className="flex items-start gap-3">
          <div className="mt-0.5 shrink-0">
            {isOfflineConfirmed ? (
              <WifiOff className="h-5 w-5 text-cede-error" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-cede-error" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-cede-text-primary">{title}</p>
              {!isOfflineConfirmed && <Loader2 className="h-3.5 w-3.5 animate-spin text-cede-text-secondary" />}
            </div>
            <p className="mt-1 text-xs text-cede-text-secondary">{subtitle}</p>

            {!isOfflineStatus && (
              <div className="mt-3 pt-3 border-t border-cede-hr flex items-center gap-2 flex-wrap">
                <span className="text-xs text-cede-text-disabled">Track status updates:</span>
                <div className="flex items-center gap-2">
                  <a
                    href={DISCORD_INVITE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleDiscordClick}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-cede-text-secondary hover:text-cede-text-primary hover:bg-cede-primary/10 transition-colors"
                  >
                    <DiscordIcon size="sm" />
                    <span>Discord</span>
                  </a>
                  <a
                    href={TWITTER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleXClick}
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-xs text-cede-text-secondary hover:text-cede-text-primary hover:bg-cede-primary/10 transition-colors"
                  >
                    <XIcon size="sm" />
                    <span>X / Twitter</span>
                  </a>
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleToggle}
            className="rounded-lg p-1.5 hover:bg-cede-primary/10 transition-colors shrink-0"
            aria-label="Collapse banner"
          >
            <ChevronUp className="h-4 w-4 text-cede-text-secondary" />
          </button>
        </div>
      )}
    </div>
  );
}
